import { useEffect, useReducer } from 'react';
import { useContextEffect, useStableCallback } from './useBetterHooks';

export const TIMER_STATUS = {
  RUNNING: 'RUNNING',
  PAUSED: 'PAUSED',
  STOPPED: 'STOPPED'
};

function reducer(state, action) {
  switch (action.type) {
    case 'advanceTime': {
      const { timeToAdd } = action.payload;

      return {
        ...state,
        time: state.timerType === 'DECREMENTAL' ? state.time - timeToAdd : state.time + timeToAdd
      };
    }
    case 'pause': {
      return {
        ...state,
        status: TIMER_STATUS.PAUSED
      };
    }
    case 'reset': {
      return {
        ...state,
        status: TIMER_STATUS.STOPPED,
        time: action.payload.initialTime
      };
    }
    case 'set': {
      return {
        ...state,
        time: action.payload.newTime
      };
    }
    case 'start': {
      const { startTime } = action.payload;

      return {
        ...state,
        status: TIMER_STATUS.RUNNING,
        time: state.status === TIMER_STATUS.STOPPED ? startTime : state.time
      };
    }
    case 'stop': {
      return {
        ...state,
        status: TIMER_STATUS.STOPPED
      };
    }
    default:
      return state;
  }
}

export const useTimer = ({
  autostart = false,
  endTime,
  initialStatus = 'STOPPED',
  initialTime = 0,
  interval = 1000,
  onTimeOver,
  onTimeUpdate,
  timerType = 'INCREMENTAL'
}) => {
  const [state, dispatch] = useReducer(reducer, {
    status: initialStatus,
    time: initialTime,
    timerType
  });

  const { status, time } = state;

  const advanceTime = useStableCallback((timeToAdd) => {
    dispatch({ type: 'advanceTime', payload: { timeToAdd } });
  });

  const pause = useStableCallback(() => {
    dispatch({ type: 'pause' });
  });

  const reset = useStableCallback(() => {
    dispatch({ type: 'reset', payload: { initialTime } });
  });

  const start = useStableCallback((startTime) => {
    dispatch({ type: 'start', payload: { startTime: startTime ?? initialTime } });
  });

  useEffect(() => {
    if (autostart) {
      dispatch({ type: 'start', payload: { initialTime } });
    }
  }, [autostart, initialTime]);

  useContextEffect({
    deps: [time],
    ctx: { time, onTimeUpdate },
    cb: (ctx) => {
      if (typeof ctx.onTimeUpdate === 'function') {
        ctx.onTimeUpdate(time);
      }
    }
  });

  useEffect(() => {
    if (status !== 'STOPPED' && time === endTime) {
      dispatch({ type: 'stop' });

      if (typeof onTimeOver === 'function') {
        onTimeOver();
      }
    }
  }, [endTime, onTimeOver, time, status]);

  useEffect(() => {
    let intervalId = null;

    if (status === 'RUNNING') {
      intervalId = setInterval(() => {
        dispatch({
          type: 'set',
          payload: {
            newTime: timerType === 'DECREMENTAL' ? time - interval : time + interval
          }
        });
      }, interval);
    } else if (intervalId) {
      clearInterval(intervalId);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [status, timerType, interval, time]);

  return { advanceTime, pause, reset, start, status, time };
};
